/**
 * SavedEnvVarSets.tsx
 *
 * Account Settings > Saved Variables. Customers store API keys and other
 * secrets that their strategies read at run time.
 *
 * WHY IT LOOKS LIKE THIS (2026-09-23 redesign):
 * - The old page was a loose stacked form with a full-width Save button and a
 *   list of bare text rows. A just-saved variable could land deep in the list
 *   and nothing confirmed where it went.
 * - Now one bounded "Add a variable" panel sits above one bounded list. Rows
 *   lead with the variable name in monospace, show a hidden-value marker,
 *   the latest activity, and explicit Replace / Delete actions.
 * - Rows are ordered by most recent activity (latest of created, updated,
 *   last used). Node orders the same way; sorting here too keeps the page
 *   correct while an older API is still serving.
 * - After a save, a confirmation names the variable and the row is marked
 *   "Just saved" at the top of the list.
 *
 * SECURITY INVARIANT: values are write-only. They exist only in the form
 * while typing, are cleared on save, and are never rendered from the API.
 */
import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faEye,
  faEyeSlash,
  faLock,
  faPen,
  faPlus,
  faTrash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { ConfirmationModal } from '../../../components/UnifiedModal';
import { Badge, Button, IconButton, Input, cn } from '../../../design-system';
import useUser from '../../../hooks/useUser';
import {
  createEnvVarSet,
  deleteEnvVarSet,
  listEnvVarSets,
  updateEnvVarSet,
} from '../../../utils/savedSecrets';
import { getErrorMessage } from '../../../utils/errorMessage';
import AccountSettingsLoadingState from '../AccountSettingsLoadingState';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  readThroughAccountSettingsCache,
} from '../accountSettingsDataCache';
import './SavedEnvVarSets.css';

interface SavedEnvVarSet {
  id: string;
  displayName: string;
  keyNames?: string[];
  status?: string;
  lastUsedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface EnvVarPayload {
  displayName: string;
  envVars?: Record<string, string>;
}

interface SaveNotice {
  id: string | null;
  keyName: string;
  verb: 'saved' | 'updated';
}

const toTime = (value?: string | null): number => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const lastActivityTime = (item: SavedEnvVarSet): number => Math.max(
  toTime(item.lastUsedAt),
  toTime(item.updatedAt),
  toTime(item.createdAt),
);

/** Active rows first, then most recent activity first. Mirrors Node's listEnvVarSets. */
export const sortByRecentActivity = (items: SavedEnvVarSet[]): SavedEnvVarSet[] => [...items].sort((left, right) => {
  const leftRank = left.status === 'disabled' ? 1 : 0;
  const rightRank = right.status === 'disabled' ? 1 : 0;
  if (leftRank !== rightRank) return leftRank - rightRank;
  const delta = lastActivityTime(right) - lastActivityTime(left);
  if (delta !== 0) return delta;
  return toTime(right.createdAt) - toTime(left.createdAt);
});

const formatDate = (time: number): string => new Date(time).toLocaleDateString(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** Plain-language label for the latest thing that happened to a variable. */
const activityLabel = (item: SavedEnvVarSet): string | null => {
  const used = toTime(item.lastUsedAt);
  const updated = toTime(item.updatedAt);
  const created = toTime(item.createdAt);
  const latest = Math.max(used, updated, created);
  if (!latest) return null;
  if (used && used === latest) return `Last used ${formatDate(used)}`;
  // updatedAt equals createdAt on a fresh row; call it Updated only when it moved.
  if (updated > created + 1000) return `Updated ${formatDate(updated)}`;
  return `Added ${formatDate(created || latest)}`;
};

const primaryKeyName = (item: SavedEnvVarSet): string => item.keyNames?.find(Boolean) || item.displayName;

const responseId = (response: unknown): string | null => {
  const record = response as { id?: unknown; item?: { id?: unknown } } | null;
  const id = record?.item?.id ?? record?.id;
  return typeof id === 'string' ? id : null;
};

const SavedEnvVarSets = () => {
  const { getApiToken, user } = useUser();
  const itemsCacheKey = makeAccountSettingsCacheKey(
    'saved-variables',
    getAccountSettingsUserCacheKey(user),
    process.env.REACT_APP_API_URL || 'api',
  );
  const cachedItems = readAccountSettingsCache<SavedEnvVarSet[]>(itemsCacheKey);
  const [items, setItems] = useState<SavedEnvVarSet[]>(() => sortByRecentActivity(cachedItems || []));
  const [loading, setLoading] = useState(() => !cachedItems);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [keyName, setKeyName] = useState('');
  const [value, setValue] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [valueRevealed, setValueRevealed] = useState(false);
  const [editingItem, setEditingItem] = useState<SavedEnvVarSet | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SavedEnvVarSet | null>(null);
  const [notice, setNotice] = useState<SaveNotice | null>(null);
  const editorRef = useRef<HTMLElement | null>(null);
  const valueInputRef = useRef<HTMLInputElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLLIElement | null>>({});

  const isEditing = Boolean(editingItem);

  const loadItems = useCallback(async (options: { force?: boolean } = {}): Promise<SavedEnvVarSet[]> => {
    const cached = readAccountSettingsCache<SavedEnvVarSet[]>(itemsCacheKey);
    if (cached && !options.force) {
      const sorted = sortByRecentActivity(cached);
      setItems(sorted);
      setError(null);
      setLoading(false);
      return sorted;
    }

    setLoading(true);
    setError(null);
    try {
      const nextItems = await readThroughAccountSettingsCache<SavedEnvVarSet[]>(itemsCacheKey, async () => {
        const token = await getApiToken();
        return await listEnvVarSets(token) as SavedEnvVarSet[];
      }, options);
      const sorted = sortByRecentActivity(nextItems);
      setItems(sorted);
      return sorted;
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load saved variables'));
      return [];
    } finally {
      setLoading(false);
    }
  }, [getApiToken, itemsCacheKey]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Bring the just-saved row into view so the customer sees where it went.
  useEffect(() => {
    if (!notice?.id) return;
    rowRefs.current[notice.id]?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [notice, items]);

  const trimmedKey = keyName.trim();
  const canSubmit = isEditing
    ? Boolean(value || displayName.trim() !== (editingItem?.displayName || ''))
    : Boolean(trimmedKey && value);
  const disabledReason = saving || canSubmit
    ? null
    : isEditing
      ? 'Enter a new value to replace the saved one.'
      : 'Enter a variable name and a value to save.';

  const resetForm = (): void => {
    setKeyName('');
    setValue('');
    setDisplayName('');
    setValueRevealed(false);
    setEditingItem(null);
  };

  const beginEdit = (item: SavedEnvVarSet): void => {
    setEditingItem(item);
    setKeyName(primaryKeyName(item));
    setDisplayName(item.displayName);
    setValue('');
    setValueRevealed(false);
    setError(null);
    setNotice(null);
    editorRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    valueInputRef.current?.focus();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!canSubmit) return;
    const savedKey = trimmedKey;
    const payload: EnvVarPayload = { displayName: displayName.trim() || savedKey || 'Saved variable' };
    if (savedKey && value) {
      payload.envVars = { [savedKey]: value };
    }

    try {
      setSaving(true);
      setError(null);
      setNotice(null);
      const token = await getApiToken();
      const response = isEditing && editingItem
        ? await updateEnvVarSet(token, editingItem.id, payload)
        : await createEnvVarSet(token, payload);
      const wasEditing = isEditing;
      const editedId = editingItem?.id || null;
      resetForm();
      const refreshed = await loadItems({ force: true });
      // Prefer the id the API returned; fall back to the newest row with this name.
      const savedId = responseId(response)
        || editedId
        || refreshed.find((item) => item.keyNames?.includes(savedKey))?.id
        || null;
      setNotice({ id: savedId, keyName: savedKey, verb: wasEditing ? 'updated' : 'saved' });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save the variable'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    try {
      setDeletingId(pendingDelete.id);
      setError(null);
      setNotice(null);
      const token = await getApiToken();
      await deleteEnvVarSet(token, pendingDelete.id);
      if (editingItem?.id === pendingDelete.id) resetForm();
      setPendingDelete(null);
      await loadItems({ force: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete the variable'));
    } finally {
      setDeletingId(null);
    }
  };

  const editingKey = editingItem ? primaryKeyName(editingItem) : '';
  const pendingDeleteKey = pendingDelete ? primaryKeyName(pendingDelete) : '';

  return (
    <div className="saved-vars">
      <div className="settings-subpage-header saved-env-var-sets-settings__header">
        <div className="settings-subpage-header-left">
          <h2 className="settings-subpage-title">Saved Variables</h2>
          <p className="settings-subpage-description">
            Keep API keys and other secrets your strategies need. After you save one, nobody can see the value again, not even you.
          </p>
        </div>
      </div>

      {error && (
        <div className="bsui-alert bsui-alert--danger saved-vars-alert" role="alert">
          <span>{error}</span>
          <IconButton label="Dismiss error" className="saved-vars-alert__dismiss" onClick={() => setError(null)}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </div>
      )}

      <section
        ref={editorRef}
        className={cn('bsui-panel saved-vars-editor', isEditing && 'saved-vars-editor--editing')}
        aria-labelledby="saved-vars-editor-title"
      >
        <div className="saved-vars-editor__head">
          <h3 id="saved-vars-editor-title" className="saved-vars-editor__title">
            {isEditing ? `Replace ${editingKey}` : 'Add a variable'}
          </h3>
          <p className="saved-vars-editor__hint">
            {isEditing
              ? 'Type the new value. It replaces the old one, which stays hidden.'
              : 'Only the name stays visible after you save.'}
          </p>
        </div>

        <form className="saved-vars-editor__form" onSubmit={handleSubmit}>
          <div className="saved-vars-editor__fields">
            <div className="bsui-field saved-vars-editor__field">
              <label className="bsui-field__label" htmlFor="saved-vars-key">Variable name</label>
              <Input
                id="saved-vars-key"
                className="saved-vars-editor__key-input"
                value={keyName}
                onChange={(event) => setKeyName(event.target.value.toUpperCase())}
                placeholder="GEMINI_API_KEY"
                aria-label="Environment variable name"
                autoComplete="off"
                spellCheck={false}
                disabled={isEditing}
              />
            </div>

            <div className="bsui-field saved-vars-editor__field">
              <label className="bsui-field__label" htmlFor="saved-vars-value">Value</label>
              <div className="saved-vars-editor__secret">
                <Input
                  id="saved-vars-value"
                  ref={valueInputRef}
                  className="saved-vars-editor__secret-input"
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  placeholder={isEditing ? 'New value' : 'Paste the secret'}
                  type={valueRevealed ? 'text' : 'password'}
                  aria-label="Environment variable value"
                  autoComplete="new-password"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className="saved-vars-editor__reveal"
                  onClick={() => setValueRevealed((current) => !current)}
                  aria-label={`${valueRevealed ? 'Hide' : 'Show'} environment variable value`}
                  aria-pressed={valueRevealed}
                >
                  <FontAwesomeIcon icon={valueRevealed ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>

            <div className="bsui-field saved-vars-editor__field">
              <label className="bsui-field__label" htmlFor="saved-vars-nickname">
                Nickname <span className="saved-vars-editor__optional">(optional)</span>
              </label>
              <Input
                id="saved-vars-nickname"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="My Gemini key"
                maxLength={160}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="saved-vars-editor__actions">
            <p className="saved-vars-editor__requirement" aria-live="polite">
              {disabledReason}
            </p>
            <div className="saved-vars-editor__buttons">
              {isEditing && (
                <Button variant="ghost" onClick={resetForm}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="primary"
                loading={saving}
                disabled={!canSubmit}
                icon={saving ? undefined : <FontAwesomeIcon icon={isEditing ? faCircleCheck : faPlus} />}
              >
                {saving ? 'Saving...' : isEditing ? 'Save New Value' : 'Save Variable'}
              </Button>
            </div>
          </div>
        </form>
      </section>

      <section className="saved-vars-list-section" aria-labelledby="saved-vars-list-title">
        <div className="saved-vars-list-section__head">
          <h3 id="saved-vars-list-title" className="saved-vars-list-section__title">Your variables</h3>
          {!loading && items.length > 0 && (
            <span className="saved-vars-list-section__count">{items.length}</span>
          )}
          <span className="saved-vars-list-section__order">Newest activity first</span>
        </div>

        {notice && (
          <div
            className="bsui-alert bsui-alert--success saved-vars-notice"
            role="status"
            aria-label="Save confirmation"
          >
            <FontAwesomeIcon icon={faCircleCheck} className="saved-vars-notice__icon" />
            <span>
              <strong className="saved-vars-notice__key">{notice.keyName}</strong> {notice.verb}.
              {' '}It is at the top of your list.
            </span>
          </div>
        )}

        {loading ? (
          <AccountSettingsLoadingState
            className="saved-vars-loading"
            label="Loading saved variables"
            text="Loading saved variables..."
          />
        ) : items.length === 0 ? (
          <div className="bsui-panel saved-vars-empty">
            <FontAwesomeIcon icon={faLock} className="saved-vars-empty__icon" />
            <p className="saved-vars-empty__title">No saved variables yet</p>
            <p className="saved-vars-empty__copy">Add your first API key above. Your strategies can use it on every run.</p>
          </div>
        ) : (
          <ul className="bsui-panel saved-vars-list">
            {items.map((item) => {
              const keyNames = (item.keyNames || []).filter(Boolean);
              const mainKey = primaryKeyName(item);
              const showNickname = item.displayName && !keyNames.includes(item.displayName);
              const justSaved = notice?.id === item.id;
              const disabled = item.status === 'disabled';
              const activity = activityLabel(item);
              return (
                <li
                  key={item.id}
                  ref={(node) => { rowRefs.current[item.id] = node; }}
                  data-testid="saved-vars-row"
                  data-saved-var-id={item.id}
                  className={cn(
                    'saved-vars-row',
                    justSaved && 'saved-vars-row--just-saved',
                    disabled && 'saved-vars-row--disabled',
                  )}
                >
                  <div className="saved-vars-row__main">
                    <div className="saved-vars-row__keys">
                      {(keyNames.length > 0 ? keyNames : [item.displayName]).map((name) => (
                        <code key={name} className="saved-vars-row__key">{name}</code>
                      ))}
                      {justSaved && <Badge variant="success">Just saved</Badge>}
                      {disabled && <Badge variant="neutral">Disabled</Badge>}
                    </div>
                    {showNickname && <div className="saved-vars-row__nickname">{item.displayName}</div>}
                  </div>

                  <div className="saved-vars-row__meta">
                    <span className="saved-vars-row__hidden" role="img" aria-label="Value hidden">
                      <FontAwesomeIcon icon={faLock} />
                      <span aria-hidden="true" className="saved-vars-row__dots">••••••••</span>
                    </span>
                    {activity && <span className="saved-vars-row__activity">{activity}</span>}
                  </div>

                  <div className="saved-vars-row__actions">
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<FontAwesomeIcon icon={faPen} />}
                      aria-label={`Replace value for ${mainKey}`}
                      onClick={() => beginEdit(item)}
                    >
                      Replace Value
                    </Button>
                    <IconButton
                      label={`Delete ${mainKey}`}
                      className="saved-vars-row__delete"
                      disabled={deletingId === item.id}
                      onClick={() => setPendingDelete(item)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <ConfirmationModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        title="Delete saved variable"
        message={pendingDelete ? `Delete ${pendingDeleteKey}? New strategy runs will no longer get it.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isProcessing={Boolean(deletingId)}
        children={null}
      />
    </div>
  );
};

export default SavedEnvVarSets;
